/**
 * Model-specific video constraints (§10).
 *
 * Pure functions — no React, no hooks, trivially testable.
 */

/* ── Constants ── */

const SEEDANCE_MODELS = ['seedance-2.0-image-to-video', 'seedance-2.0-reference-to-video']
const SEEDANCE_DURATIONS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15]
const SEEDANCE_RESOLUTIONS = ['480p', '720p']
const SEEDANCE_DEFAULT_DURATION = 15

const KLING_DURATIONS = [4, 5, 6, 7, 8, 9, 10, 11, 12]
const VEO_720P_DURATIONS = [4, 6, 8]
const VEO_FIXED_DURATION = 8
const VEO_RESOLUTIONS = ['720p', '1080p', '4k']

const KLING_DEFAULT_DURATION = 5
const VEO_DEFAULT_DURATION = 8

/* ── Types ── */

export interface VideoConstraints {
  /** Available resolution options, or null if resolution is a static label (e.g. Kling "Auto"). */
  resolutionOptions: string[] | null
  /** Default resolution for this model. */
  resolutionDefault: string
  /** Static resolution label when resolutionOptions is null. */
  resolutionLabel?: string
  /** Available duration options (seconds), or null if duration is a static label. */
  durationOptions: number[] | null
  /** Default duration for this model+resolution combo. */
  durationDefault: number
  /** Static duration label when durationOptions is null (e.g. "8s"). */
  durationLabel?: string
}

/* ── Main Functions ── */

/**
 * Get available options and defaults for a given model and resolution.
 *
 * - Seedance 2.0: resolution selectable [480p, 720p], duration 4-15s, default 15s.
 * - Kling: resolution fixed "Auto", duration 4-12s, default 5s.
 * - Veo 720p: resolution selectable [720p, 1080p, 4k], duration {4,6,8}, default 8s.
 * - Veo 1080p/4k: resolution selectable, duration fixed 8s.
 */
export function getVideoConstraints(model: string, resolution?: string): VideoConstraints {
  if (isSeedance(model)) {
    return {
      resolutionOptions: [...SEEDANCE_RESOLUTIONS],
      resolutionDefault: '720p',
      durationOptions: [...SEEDANCE_DURATIONS],
      durationDefault: SEEDANCE_DEFAULT_DURATION,
    }
  }

  if (model === 'kling-v3-pro') {
    return {
      resolutionOptions: null,
      resolutionDefault: 'auto',
      resolutionLabel: 'Auto',
      durationOptions: [...KLING_DURATIONS],
      durationDefault: KLING_DEFAULT_DURATION,
    }
  }

  // Veo 3.1
  const effectiveResolution = resolution || '720p'

  if (effectiveResolution === '720p') {
    return {
      resolutionOptions: [...VEO_RESOLUTIONS],
      resolutionDefault: '720p',
      durationOptions: [...VEO_720P_DURATIONS],
      durationDefault: VEO_DEFAULT_DURATION,
    }
  }

  // 1080p or 4k
  return {
    resolutionOptions: [...VEO_RESOLUTIONS],
    resolutionDefault: effectiveResolution,
    durationOptions: null,
    durationDefault: VEO_FIXED_DURATION,
    durationLabel: '8s',
  }
}

/**
 * Snap resolution and duration when switching models.
 *
 * - Any → Seedance: resolution='720p' (if not already 480p/720p), duration clamped to [4,15].
 * - Any → Kling: resolution='auto', duration clamped to [4,12].
 * - Any → Veo: resolution='720p', duration snapped to nearest in {4,6,8}.
 *   Ties go to the lower value (strict <).
 */
export function snapOnModelChange(
  fromModel: string,
  toModel: string,
  current: { duration: number; resolution: string },
): { duration: number; resolution: string } {
  if (fromModel === toModel) return current

  if (isSeedance(toModel)) {
    // Any → Seedance: snap resolution to 720p if not already 480p/720p
    const validSeedanceRes = ['480p', '720p']
    const resolution = validSeedanceRes.includes(current.resolution)
      ? current.resolution
      : '720p'
    return {
      resolution,
      duration: Math.max(4, Math.min(15, current.duration)),
    }
  }

  if (toModel === 'kling-v3-pro') {
    // Any → Kling
    return {
      resolution: 'auto',
      duration: Math.max(4, Math.min(12, current.duration)),
    }
  }

  // Any → Veo
  return {
    resolution: '720p',
    duration: snapToNearest(current.duration, VEO_720P_DURATIONS),
  }
}

/**
 * Snap duration when resolution changes within a model.
 *
 * - Veo 720p → 1080p/4k: duration snaps to 8s.
 * - Veo 1080p/4k → 720p: duration stays (8s is already valid for 720p).
 * - Seedance: resolution change doesn't affect duration (both 480p and 720p support 4-15s).
 * - Non-Veo/non-Seedance models: returns currentDuration unchanged.
 */
export function snapOnResolutionChange(
  model: string,
  newResolution: string,
  currentDuration: number,
): number {
  // Seedance: resolution change doesn't affect duration range
  if (isSeedance(model)) return currentDuration

  if (model !== 'veo-3.1') return currentDuration

  if (newResolution === '1080p' || newResolution === '4k') {
    return VEO_FIXED_DURATION
  }

  // 720p — current duration should already be valid (8s), but snap just in case
  if (!VEO_720P_DURATIONS.includes(currentDuration)) {
    return snapToNearest(currentDuration, VEO_720P_DURATIONS)
  }

  return currentDuration
}

/* ── Helpers ── */

function isSeedance(model: string): boolean {
  return SEEDANCE_MODELS.includes(model)
}

/**
 * Find the nearest value in a sorted array. On ties (equal distance to two values),
 * prefer the lower value (strict < comparison).
 */
function snapToNearest(value: number, options: readonly number[]): number {
  let best = options[0]
  let bestDist = Math.abs(value - best)

  for (let i = 1; i < options.length; i++) {
    const dist = Math.abs(value - options[i])
    if (dist < bestDist) {
      best = options[i]
      bestDist = dist
    }
  }

  return best
}
